import { createFileRoute, redirect } from '@tanstack/react-router'
import { findCachedVideo, video } from '@/lib/queries/videos'
import { decodeId, encodeId } from '@/lib/short-uuid'
import { VideoDetailPage } from '@/features/workspaces/video/detail'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/workspace/$workspaceId/video/$videoId'
)({
  params: {
    parse: (rawParams: { videoId: string }) => {
      try {
        return { videoId: decodeId(rawParams.videoId) }
      } catch {
        return { videoId: rawParams.videoId }
      }
    },
    stringify: (params: { videoId: string }) => ({
      videoId: encodeId(params.videoId),
    }),
  },
  loader: async ({ context, params }) => {
    const { queryClient } = context

    try {
      // Check infinite cache first (for optimistic updates)
      const cachedData = findCachedVideo(
        queryClient,
        params.workspaceId,
        params.videoId
      )

      // ALWAYS ensure the query is properly initialized with its queryFn
      // This is critical for invalidateQueries to work later
      const queryOptions = video.findOne(params.videoId)

      // Only seed if no data exists yet (don't overwrite fresher data from realtime refetch)
      if (cachedData && !queryClient.getQueryData(queryOptions.queryKey)) {
        queryClient.setQueryData(queryOptions.queryKey, cachedData)
      }

      const data = await queryClient.query({
        ...queryOptions,
        staleTime: 'static',
      })

      if (!data || data.workspace_id !== params.workspaceId) {
        throw new Error('Invalid video')
      }
    } catch {
      throw redirect({
        to: '/org/$orgId/workspace/$workspaceId/video',
        params: { orgId: params.orgId, workspaceId: params.workspaceId },
        replace: true,
      })
    }
  },
  component: VideoDetailPage,
})
